import React, { useState } from 'react';
import {
    Typography, Paper, Container, Box,
    List, ListItem, ListItemButton, ListItemText, Avatar, IconButton,
    Divider, Button, Autocomplete, TextField, Checkbox, Chip, Grid
} from '@mui/material';
import {
    Person as PersonIcon,
    CheckBoxOutlineBlank,
    CheckBox,
    Save as SaveIcon,
    Group as TeamIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../redux/redux.hook';
import {
    setTeamForSite, removeTeamMemberFromSite
} from '../redux/team.slice';
import toast from 'react-hot-toast';
import { createFilterOptions, FilterOptionsState } from '@mui/material';
import { Employee, SiteDetail } from '../interfaces/timesheet';

// Multi-field filter function for searching through employees
const createMultiFieldFilter = () => {
    const defaultFilterOptions = createFilterOptions();

    return (options: any[], state: FilterOptionsState<unknown>) => {
        if (!state.inputValue) {
            return defaultFilterOptions(options, state);
        }

        const searchText = state.inputValue.toLowerCase();
        return options.filter(option =>
            option.name.toLowerCase().includes(searchText) ||
            option.matricule.toString().toLowerCase().includes(searchText)
        );
    };
};

export const TeamManagement: React.FC = () => {
    const dispatch = useAppDispatch();
    const { data, error } = useAppSelector(state => state.data);
    const teamMembers = useAppSelector(state => state.team.teamsBySite);
    const [isSaving, setIsSaving] = useState(false);
    const [selectedWorksite, setSelectedWorksite] = useState<SiteDetail | null>(null);
    const multiFieldFilter = createMultiFieldFilter();

    const handleSaveTeam = () => {
        setIsSaving(true);

        // Simulate saving action
        setTimeout(() => {
            setIsSaving(false);
            toast.success('Équipe enregistrée avec succès!');
        }, 500);
    };

    const handleRemoveTeamMember = (worker: Employee) => {
        if (selectedWorksite) {
            dispatch(removeTeamMemberFromSite({ siteMatricule: selectedWorksite.matricule, matricule: worker.matricule }));
        }
    };

    const handleSelectWorksite = (newValue: SiteDetail | null) => {
        setSelectedWorksite(newValue);
    };

    const handleAddTeamMember = (newValue: Employee[]) => {
        if (selectedWorksite) {
            dispatch(setTeamForSite({ siteMatricule: selectedWorksite.matricule, team: newValue }));
        }
    };

    // Ensure we access the team members for the selected worksite and fallback to an empty array if not found
    const currentTeamMembers = selectedWorksite && teamMembers
        ? teamMembers[selectedWorksite.matricule] || []
        : [];

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }} style={{ padding: 0 }}>
            <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }} fontWeight={800}>
                <TeamIcon /> Mon équipe par chantier
            </Typography>

            {error && (
                <Box sx={{ mb: 3 }}>
                    <Typography color="error">{error}</Typography>
                </Box>
            )}

            <Grid container spacing={3}>
                {/* Left - Worksite Selection */}
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Sélectionner un chantier
                        </Typography>
                        <Autocomplete
                            id="worksite-autocomplete"
                            options={data?.sites || []}
                            value={selectedWorksite}
                            onChange={(event, newValue) => handleSelectWorksite(newValue)}
                            getOptionLabel={(option) => option.name || ''}
                            filterOptions={multiFieldFilter}
                            isOptionEqualToValue={(option, value) => option.matricule === value.matricule}
                            renderInput={(params) => <TextField {...params} label="Chantier" />}
                            renderOption={(props, option) => (
                                <li {...props}>
                                    {option.name} - {option.matricule}
                                </li>
                            )}
                        />
                    </Paper>
                </Grid>

                {/* Right - Team Management */}
                <Grid item xs={12} md={8}>
                    {/* Check if no worksite is selected */}
                    {!selectedWorksite ? (
                        <Paper sx={{ p: 3, mb: 3 }}>
                            <Typography variant="body1" color="text.secondary" align="center">
                                Veuillez sélectionner un chantier pour gérer l'équipe.
                            </Typography>
                        </Paper>
                    ) : (
                        <>
                            {/* Employee Selection */}
                            <Paper sx={{ p: 3, mb: 3 }}>
                                <Typography variant="h6" gutterBottom>
                                    Ajouter des employés
                                </Typography>
                                <Autocomplete
                                    multiple
                                    id="team-members-autocomplete"
                                    options={data?.employees || []}
                                    value={currentTeamMembers}
                                    onChange={(event, newValue) => handleAddTeamMember(newValue)}
                                    getOptionLabel={(option) => option.name || ''}
                                    isOptionEqualToValue={(option, value) => option.matricule === value.matricule}
                                    filterOptions={multiFieldFilter}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Sélectionner des employés"
                                            placeholder="Chercher des employés..."
                                        />
                                    )}
                                    renderOption={(props, option, { selected }) => (
                                        <li {...props}>
                                            <Checkbox
                                                icon={<CheckBoxOutlineBlank fontSize="small" />}
                                                checkedIcon={<CheckBox fontSize="small" />}
                                                style={{ marginRight: 8 }}
                                                checked={Boolean(selected)}
                                            />
                                            {option.name} - {option.matricule}
                                        </li>
                                    )}
                                    renderTags={(value, getTagProps) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {value.map((option, index) => (
                                                <Chip
                                                    {...getTagProps({ index })}
                                                    key={option.matricule}
                                                    label={option.name}
                                                    avatar={<Avatar><PersonIcon /></Avatar>}
                                                />
                                            ))}
                                        </Box>
                                    )}
                                />
                                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleSaveTeam}
                                        disabled={isSaving}
                                        startIcon={<SaveIcon />}
                                    >
                                        {isSaving ? 'Enregistrement...' : 'Enregistrer'}
                                    </Button>
                                </Box>
                            </Paper>

                            {/* Display Team Members */}
                            {selectedWorksite && currentTeamMembers.length > 0 && (
                                <Paper sx={{ p: 3 }}>
                                    <Typography variant="h6" gutterBottom>
                                        ({currentTeamMembers.length}) Membre(s) de l'équipe pour {selectedWorksite?.name}
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />
                                    <List>
                                        {currentTeamMembers.map((member) => (
                                            <ListItem
                                                key={member.matricule}
                                                secondaryAction={
                                                    <IconButton edge="end" onClick={() => handleRemoveTeamMember(member)}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                }
                                                divider
                                            >
                                                <ListItemButton>
                                                    <Avatar sx={{ mr: 2 }}><PersonIcon /></Avatar>
                                                    <ListItemText
                                                        primary={member.name}
                                                        secondary={member.role || 'Pas de rôle assigné'}
                                                    />
                                                </ListItemButton>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Paper>
                            )}
                        </>
                    )}
                </Grid>
            </Grid>
        </Container>
    );
};
